import React from 'react';
import { Box, Button, Heading } from '@wix/design-system';
import { ArrowRight } from '@wix/wix-ui-icons-common';
import { config } from '../../config';
import { Origin, Path } from '../../enums/marketplace-enums';
import { SolutionGridWrapper as AppGrid } from '../solution-grid-wrapper/solution-grid-wrapper';
import { useMarketplaceStore } from '../../component-decorators/with-marketplace-store';
import type { WebSolutionBase } from '@wix/ambassador-marketplace/types';
import { useMediaQuery } from 'react-responsive';
import { ViewAllApps } from './sub-category-section.utils';
import { biLogger } from '../../bi';
import { appMarketSubCategoryClick } from '@wix/bi-logger-market/v2';

export interface SubCategorySectionProps {
  slug: string;
  title: string;
  apps: (WebSolutionBase & { appIndex: string })[];
}

export function SubCategorySection({
  slug,
  title,
  apps,
}: SubCategorySectionProps) {
  const { route, metaSiteId } = useMarketplaceStore();
  const isBigScreen = useMediaQuery({ query: '(min-width: 1484px)' });

  function getMaxAppsInSection() {
    return isBigScreen ? 8 : 6;
  }

  function areAllAppsShown() {
    return apps.length <= getMaxAppsInSection();
  }

  function isInStandaloneMarket() {
    return config.origin === Origin.STANDALONE;
  }

  function getSubCategoryPageHref() {
    return `/app-market/${Path.CATEGORY}/${route.slug}/${slug}`;
  }

  function goToSubCategoryPage() {
    config.goto?.({
      path: Path.CATEGORY,
      slug: route.slug,
      subCategories: [slug],
      referral: 'view_more_apps',
    });
  }

  return (
    <>
      <Box direction="horizontal" align="space-between" verticalAlign="middle">
        <Heading size="medium">{title}</Heading>

        {!areAllAppsShown() ? (
          <Button
            size="small"
            suffixIcon={<ArrowRight />}
            as="a"
            href={isInStandaloneMarket() ? getSubCategoryPageHref() : undefined}
            onClick={(event) => {
              event.preventDefault();
              biLogger.report(
                appMarketSubCategoryClick({
                  type: 'view_more_apps',
                  subcategory_name: slug.toLowerCase().replace(/-/g, '_'),
                  category_name: title.toLowerCase(),
                  msid: metaSiteId,
                }),
              );
              goToSubCategoryPage();
            }}
          >
            <ViewAllApps count={apps.length} />
          </Button>
        ) : null}
      </Box>

      <AppGrid solutions={apps.slice(0, getMaxAppsInSection())} />
    </>
  );
}
